import { creatorbase } from '@round/api';
import { ComboboxItem } from '@round/creatorbase-ui';
import { usePoolsListInfinite } from '@round/creatorbase-helpers/api-hooks';
import debounce from 'lodash/debounce';
import { UIEventHandler, useCallback, useMemo, useState } from 'react';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;
const LIST_SCROLL_THRESHOLD_PX = 80;

export type PoolItem = ComboboxItem<number> & Pick<creatorbase.Pool, 'description' | 'member_count'>;

type Params = {
    onSelect: (pool: creatorbase.Pool) => void;
    memberPoolIds?: number[];
    /**
     * The pool to show as picked, for a caller that holds on to its pick until the user acts on it.
     * Left out, nothing stays picked and every pick only fires `onSelect`.
     */
    value?: creatorbase.Pool | null;
};

const mapPoolToItem = (pool: creatorbase.Pool): PoolItem => ({
    value: pool.id,
    label: pool.name,
    description: pool.description,
    member_count: pool.member_count,
});

const isSamePool = (a: PoolItem | null, b: PoolItem | null) => a?.value === b?.value;

/** The client's pools, searchable and paged, for adding accounts to one of them. */
export function usePoolSearchCombobox({ onSelect, memberPoolIds = [], value = null }: Params) {
    const { selectedClientId } = useClientSelect();
    const [isOpen, setIsOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const setSearch = useMemo(() => debounce(setDebouncedSearch, SEARCH_DEBOUNCE_MS), []);

    const { data, isLoading, hasError, hasNextPage, isFetchingNextPage, fetchNextPage } = usePoolsListInfinite(
        { client_id: selectedClientId, page_size: PAGE_SIZE, search: debouncedSearch },
        { enabled: isNumber(selectedClientId) && isOpen }
    );

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const element = event.currentTarget;
            if (element.scrollHeight - element.scrollTop - element.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }

            if (hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage]
    );

    // The input clears when the popup closes, so the search term has to clear with it, and a
    // keystroke from just before closing must not land against a disabled query.
    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (!open) {
                setSearch.cancel();
                setDebouncedSearch('');
            }
        },
        [setSearch]
    );

    const isMember = (poolId: number) => memberPoolIds.includes(poolId);

    const handleValueChange = (item: PoolItem | PoolItem[] | null) => {
        if (!item || Array.isArray(item) || isMember(item.value)) {
            return;
        }

        const pool = data.find(({ id }) => id === item.value);
        if (pool) {
            onSelect(pool);
        }
    };

    return {
        rootProps: {
            items: data.map(mapPoolToItem),
            value: value ? mapPoolToItem(value) : null,
            // The picked item is rebuilt from the pool on every render, so it is matched by id.
            isItemEqualToValue: isSamePool,
            /** Disables base-ui client filtering; the list is driven by the debounced API search. */
            filter: null,
            open: isOpen,
            onOpenChange: handleOpenChange,
            onInputValueChange: useCallback((value: string) => setSearch(value), [setSearch]),
            onValueChange: handleValueChange,
        },
        isMember,
        onItemsListScroll,
        // The empty label doubles as the loading label, since an in-flight search has no items yet
        noItemsFound: hasError ? 'Could not get pools' : isLoading ? 'Searching pools...' : 'No pools found',
    };
}
